import React from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { AppProvider } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';
import { Frame } from '@shopify/polaris';

// Components
import AuthPage from './pages/auth/AuthPage';
import Register from './pages/auth/Register';
import MainApp from './layouts/MainApp';
import ShopManager from './pages/ShopManager';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import BulkEditProduct from './pages/BulkEditProduct';
import BulkEditImages from './pages/BulkEditImages';
import Settings from './pages/Settings';
import OAuthCallback from './pages/OAuthCallback';
import ShopDetails from './pages/ShopDetails';
import ShopConnect from './pages/ShopConnect';
import SystemSettingsPage from './pages/admin/SystemSettingsPage';
import ServerStatusPage from './pages/admin/ServerStatusPage';
import AIModelManagement from './pages/admin/AIModelManagement';
import BetaManagementPage from './pages/admin/BetaManagementPage';
import AppLayout from './components/AppLayout';
import AdminPanel from './pages/AdminPanel';
import ShopMetafields from './pages/ShopMetafields';
import SubscriptionPlans from './pages/SubscriptionPlans';
import FlexCredits from './pages/FlexCredits';
import PaymentHistory from './pages/PaymentHistory';
import Changelog from './pages/Changelog';
import SizechartManager from './pages/sizecharts/SizechartManager';
import TrackingAdmin from './pages/TrackingAdmin';
import MaintenancePage from './pages/MaintenancePage';
import PublicRoute from './components/PublicRoute';
import PrivateRoute from './components/PrivateRoute';
// Landing Pages
import Home from './pages/landing/Home';
import AIEngines from './pages/landing/AIEngines';
import Templates from './pages/landing/Templates';
import HowItWorks from './pages/landing/HowItWorks';
import Pricing from './pages/landing/Pricing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ContactUs from './pages/ContactUs';
import Footer from './components/Footer';
import LandingPageWrapper from './components/LandingPageWrapper';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { ShopProvider } from './contexts/ShopContext';
import { TemplateProvider } from './contexts/TemplateContext';
import { ResultsProvider } from './contexts/ResultsContext';

function App() {
  const handleProductChange = () => {
    // implement handleProductChange function
  }
  
  return (
    <AppProvider i18n={enTranslations}>
      <BrowserRouter>
        <Routes>
          {/* Standalone public routes that don't need authentication */}
          <Route path="/privacy-policy" element={<><LandingPageWrapper><PrivacyPolicy /></LandingPageWrapper><Footer /></>} />
          <Route path="/terms-of-service" element={<><LandingPageWrapper><TermsOfService /></LandingPageWrapper><Footer /></>} />
          <Route path="/contact-us" element={<><LandingPageWrapper><ContactUs /></LandingPageWrapper><Footer /></>} />
          
          {/* Standalone OAuth callback page (no app layout, just the callback component) */}
          <Route path="/app/oauth-callback" element={<AuthProvider><OAuthCallback /></AuthProvider>} />
          
          {/* Landing Pages - Outside auth context */}
          <Route path="/" element={<><LandingPageWrapper><Home /></LandingPageWrapper><Footer /></>} />
          <Route path="/ai-engines" element={<><LandingPageWrapper><AIEngines /></LandingPageWrapper><Footer /></>} />
          <Route path="/templates" element={<><LandingPageWrapper><Templates /></LandingPageWrapper><Footer /></>} />
          <Route path="/how-it-works" element={<><LandingPageWrapper><HowItWorks /></LandingPageWrapper><Footer /></>} />
          <Route path="/pricing" element={<><LandingPageWrapper><Pricing /></LandingPageWrapper><Footer /></>} />
          
          {/* Special routes for register with referral codes - both with and without /app prefix */}
          <Route path="/app/register" element={
            <AuthProvider>
              <LandingPageWrapper>
                <Register />
              </LandingPageWrapper>
              <Footer />
            </AuthProvider>
          } />
          
          {/* Shorter URL format for referral links */}
          <Route path="/register" element={
            <AuthProvider>
              <LandingPageWrapper>
                <Register />
              </LandingPageWrapper>
              <Footer />
            </AuthProvider>
          } />
          
          {/* Routes that use authentication context */}
          <Route path="*" element={
            <AuthProvider>
              <ShopProvider>
                <TemplateProvider handleProductChange={handleProductChange}>
                  <Frame>
                    <Routes>
                      <Route path="/auth" element={<AuthPage />} />
                      <Route path="/maintenance" element={<MaintenancePage />} />
                      
                      {/* Protected routes */}
                      <Route path="/app" element={<PrivateRoute><AppLayout /></PrivateRoute>}>
                        <Route index element={<Dashboard />} />
                        <Route path="products" element={<Products />} />
                        <Route path="product/bulk-edit" element={<BulkEditProduct />} />
                        <Route path="product/bulk-edit-images" element={<BulkEditImages />} />
                        <Route path="settings" element={<Settings />} />
                        <Route path="oauth-callback" element={<OAuthCallback />} />
                        <Route path="shops" element={<ShopManager />} />
                        <Route path="shops/:shopId" element={<ShopDetails />} />
                        <Route path="shops/:shopId/metafields" element={<ShopMetafields />} />
                        <Route path="connect" element={<ShopConnect />} />
                        <Route path="subscription" element={<SubscriptionPlans />} />
                        <Route path="credits" element={<FlexCredits />} />
                        <Route path="payment-history" element={<PaymentHistory />} />
                        <Route path="changelog" element={<Changelog />} />
                        <Route path="sizecharts" element={<SizechartManager />} />
                        <Route path="tracking-admin" element={
                          <PrivateRoute adminOnly={true}>
                            <TrackingAdmin />
                          </PrivateRoute>
                        } />
                        <Route path="admin" element={
                          <PrivateRoute adminOnly={true}>
                            <AdminPanel />
                          </PrivateRoute>
                        } />
                      </Route>
                      {/* Catch-all route for any other paths */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <Footer />
                  </Frame>
                </TemplateProvider>
              </ShopProvider>
            </AuthProvider>
          } />
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
